import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { get, STATUS_LABEL } from "../api";
import { useAuth } from "../auth";
import Sources, { useSources } from "./Sources";
import Platform from "./Platform";
import type { SourcesController } from "./Sources";
import TrackForm from "./TrackForm";
import type {
  Assignable,
  EvidenceRef,
  Finding,
  ReportAction,
  ReportDetail,
  ReportSentiment,
  Suggestion,
  SuggestionList,
} from "../api";

/**
 * A report, laid out by the app rather than embedded as a document.
 *
 * It used to be an iframe holding `sage_report::render`'s HTML, sandboxed
 * because its source-material appendix is ingested content. That bought
 * isolation and cost a page that printed the customer's name twice, a
 * column half the width of the one around it, a postMessage protocol to
 * size the frame, and evidence links that could not have reached out of it
 * anyway. Rendering here means a finding can carry a button, an evidence
 * ref is a link into the page, and there is one scrollbar.
 *
 * The escaping that the renderer did with `esc` is now JSX's: every string
 * from the report or the corpus lands in a text node. `dangerouslySetInnerHTML`
 * would undo that in one line, which is why `make web-check` refuses it.
 *
 * `sage_report::render` still exists and still produces the standalone
 * document — the CLI writes it, and "Open full report" serves it. The two
 * renderings are allowed to drift; they read the same `reports.report` row.
 */
export default function ReportView({
  detail,
  customer,
  lifetimeRuns,
  lifetimeSpend,
  onTracked,
}: {
  detail: ReportDetail;
  /** Slug or uuid, as it appears in the URL. */
  customer: string;
  lifetimeRuns: number | null;
  lifetimeSpend: number | null;
  onTracked: () => void;
}) {
  const r = detail.report;
  const sources = useSources(customer, detail.id);
  // Admin-only, and the server agrees: it serves a CSM a report whose cost
  // fields are null, so this hides a section that would have nothing in it.
  const { me } = useAuth();
  const showCost = me?.user.is_admin ?? false;

  return (
    <div className="report">
      {detail.uncovered_days > 0 && (
        <div className="banner">
          <b>
            {detail.uncovered_days} day{detail.uncovered_days === 1 ? "" : "s"} not covered by
            any report.
          </b>{" "}
          The window is capped at 7 days and the last report ended before that, so activity in
          between was never reviewed.
        </div>
      )}

      <div className="stats">
        <Stat n={detail.slack_messages} label="Slack messages" />
        <Stat n={detail.email_messages} label="emails" />
        <Stat n={r.highlights.length} label="highlights" />
        <Stat n={r.lowlights.length} label="lowlights" />
        <Stat n={r.action_items.length} label="action items" />
        <div className="stat">
          <span className={`badge ${SENTIMENT[r.sentiment] ?? "neu"}`}>
            {cap(r.sentiment)}
          </span>
          <span className="statlabel">sentiment</span>
        </div>
      </div>

      <div className="summary">{r.executive_summary}</div>

      <h2>Highlights</h2>
      <Findings items={r.highlights} kind="highlight" sources={sources} />

      <h2>Lowlights</h2>
      <Findings items={r.lowlights} kind="lowlight" sources={sources} />

      {r.platform && <Platform brief={r.platform} />}

      <h2>Action items</h2>
      <ActionItems
        actions={r.action_items}
        customer={customer}
        reportId={detail.id}
        sources={sources}
        onTracked={onTracked}
      />

      {r.open_questions.length > 0 && (
        <>
          <h2>Open questions</h2>
          <ul className="oq">
            {r.open_questions.map((q, i) => (
              <li key={i}>{q}</li>
            ))}
          </ul>
        </>
      )}

      <Sources s={sources} />

      {showCost && (
        <RunCost detail={detail} runs={lifetimeRuns ?? 0} spend={lifetimeSpend ?? 0} />
      )}

      <footer className="reportfoot">
        Written by an AI agent from the Slack and email collected for this customer over the
        period above — check the evidence behind a finding before acting on it.
      </footer>
    </div>
  );
}

const SENTIMENT: Record<ReportSentiment, string> = {
  positive: "pos",
  neutral: "neu",
  mixed: "mix",
  negative: "neg",
};

/** "high" -> "High". The wire carries lowercase enums. */
const cap = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);

function Stat({ n, label }: { n: number | null; label: string }) {
  return (
    <div className="stat">
      <b>{n ?? "—"}</b>
      <span className="statlabel">{label}</span>
    </div>
  );
}

// --- Findings -----------------------------------------------------------

function Findings({
  items,
  kind,
  sources,
}: {
  items: Finding[];
  kind: "highlight" | "lowlight";
  sources: SourcesController;
}) {
  if (items.length === 0) {
    return <p className="dim">Nothing recorded as a {kind} this period.</p>;
  }
  return (
    <>
      {items.map((f, i) => (
        <article key={i} className={`finding ${kind}`}>
          <h3>{f.title}</h3>
          <p>{f.detail}</p>
          <Evidence items={f.evidence} sources={sources} />
        </article>
      ))}
    </>
  );
}

/**
 * Where a finding came from, as a link into the source material.
 *
 * A ref pointing at a thread the appendix does not hold was invented by the
 * agent, and renders as plain text rather than a dead link — visibly
 * unsupported instead of quietly broken. Until the sources are loaded we
 * cannot know which is which, so everything is a link and clicking one
 * loads them; `known` turning non-null is what marks the bad ones.
 */
function Evidence({ items, sources }: { items: EvidenceRef[]; sources: SourcesController }) {
  if (items.length === 0) return null;
  return (
    <ul className="ev">
      {items.map((e, i) => {
        const quote = e.quote?.trim();
        const text = quote ? `${e.source} “${quote}”` : e.source;
        const missing = sources.known !== null && !sources.known.has(e.thread_id);
        return (
          <li key={i} className={missing ? "unlinked" : undefined}>
            {missing ? (
              <>
                {text} <span className="warn">(source not in this report)</span>
              </>
            ) : (
              <a
                href={`#thread-${e.thread_id}`}
                onClick={(ev) => {
                  // The browser would jump to an element that is not
                  // rendered yet: the section is collapsed until asked for.
                  ev.preventDefault();
                  sources.reveal(e.thread_id);
                }}
              >
                {text}
              </a>
            )}
          </li>
        );
      })}
    </ul>
  );
}

// --- Action items -------------------------------------------------------

/**
 * The agent's proposed actions, each with the item it became.
 *
 * Rendered once, here, with the Track button beside it. Invariant 30 is the
 * reason the button exists at all rather than the report writing rows: a
 * suggestion is an opinion the next run may drop, and an item somebody owns
 * must not vanish with it.
 *
 * The tracked state comes from `/suggestions` rather than the report row,
 * because it is a fact about the tracker and changes without the report
 * changing. Matching is by `action_id`, the writer-assigned stable id.
 */
function ActionItems({
  actions,
  customer,
  reportId,
  sources,
  onTracked,
}: {
  actions: ReportAction[];
  customer: string;
  reportId: string;
  sources: SourcesController;
  onTracked: () => void;
}) {
  const [tracked, setTracked] = useState<Map<string, Suggestion>>(new Map());
  const [users, setUsers] = useState<Assignable[]>([]);
  const [openForm, setOpenForm] = useState<string | null>(null);

  const load = useCallback(() => {
    void get<SuggestionList>(`/api/customers/${customer}/reports/${reportId}/suggestions`)
      .then((d) =>
        setTracked(new Map(d.suggestions.filter((s) => s.action_id).map((s) => [s.action_id!, s]))),
      )
      .catch(() => setTracked(new Map()));
  }, [customer, reportId]);

  useEffect(load, [load]);

  useEffect(() => {
    void get<{ users: Assignable[] }>(`/api/customers/${customer}/assignees`)
      .then((d) => setUsers(d.users))
      .catch(() => setUsers([]));
  }, [customer]);

  if (actions.length === 0) return <p className="dim">No action items.</p>;

  return (
    <>
      {actions.map((a, i) => {
        const state = a.id ? tracked.get(a.id) : undefined;
        const meta = [
          a.owner ? `Owner: ${a.owner}` : null,
          a.due ? `Due: ${a.due}` : null,
        ].filter(Boolean);

        return (
          <article key={a.id ?? i} className={`finding action p-${a.priority} ag-${a.status}`}>
            <div className="ahead">
              <span className={`pill ${a.priority}`}>{cap(a.priority)}</span>
              <span className={`pill ag-${a.status}`}>{cap(a.status)}</span>
              <h3>{a.title}</h3>
              {state?.tracked_id ? (
                <Link className="btn tiny" to={`/actions/${state.tracked_id}`}>
                  Tracked · {state.tracked_status ? STATUS_LABEL[state.tracked_status] : ""} →
                </Link>
              ) : (
                <button
                  className="tiny primary"
                  disabled={!a.id}
                  title={
                    a.id
                      ? undefined
                      : "This report predates stable suggestion ids, so it cannot be tracked by identity."
                  }
                  onClick={() => setOpenForm(openForm === a.id ? null : (a.id ?? null))}
                >
                  Track this
                </button>
              )}
            </div>

            <p>{a.detail}</p>
            <p className="meta">
              {meta.length > 0 ? (
                meta.join(" · ")
              ) : (
                // Said plainly, because a blank owner reads as an oversight
                // when it is actually a fact about the source material.
                <span className="unset">No owner or due date stated in the conversation</span>
              )}
              {/* An item open for four days is a different fact from a new
                  one, and the prose rarely makes that distinction itself. */}
              {a.carried_from && a.first_seen && (
                <span className="since"> · open since {a.first_seen}</span>
              )}
            </p>

            <Evidence items={a.evidence} sources={sources} />

            {openForm && openForm === a.id && (
              <TrackForm
                action={a}
                users={users}
                customer={customer}
                reportId={reportId}
                onDone={() => {
                  setOpenForm(null);
                  load();
                  onTracked();
                }}
              />
            )}
          </article>
        );
      })}
    </>
  );
}

// --- Run cost -----------------------------------------------------------

/** Sub-cent runs are the normal case for this model, so rounding them to
    $0.00 would make the line useless. */
function money(v: number | null | undefined): string {
  if (v === null || v === undefined) return "—";
  return v > 0 && v < 0.01 ? `$${v.toFixed(6)}` : `$${v.toFixed(4)}`;
}

const num = (v: number | null) => (v === null ? "—" : v.toLocaleString());

function RunCost({
  detail: d,
  runs,
  spend,
}: {
  detail: ReportDetail;
  runs: number;
  spend: number;
}) {
  return (
    <>
      <h2>Run cost</h2>
      <table className="cost">
        <tbody>
          <tr>
            <td>Mode</td>
            <td className="num">{d.mode}</td>
          </tr>
          <tr>
            <td>Model</td>
            <td className="num">{d.model ?? "—"}</td>
          </tr>
          <tr>
            <td>LLM calls</td>
            <td className="num">{num(d.llm_calls)}</td>
          </tr>
          <tr>
            <td>Tool calls</td>
            <td className="num">{num(d.tool_calls)}</td>
          </tr>
          <tr>
            <td>Input tokens ({num(d.cached_input_tokens)} cached)</td>
            <td className="num">{num(d.input_tokens)}</td>
          </tr>
          <tr>
            <td>Output tokens</td>
            <td className="num">{num(d.output_tokens)}</td>
          </tr>
          <tr>
            <td>Wall clock</td>
            <td className="num">
              {d.duration_ms === null ? "—" : `${(d.duration_ms / 1000).toFixed(1)}s`}
            </td>
          </tr>
          <tr>
            <td>
              <b>Total cost</b>
            </td>
            <td className="num">
              <b>{money(d.cost_usd)}</b>
            </td>
          </tr>
          {/* Invariant 16: the total is computed from tokens, and this is
              the account delta measured across the run. When they disagree,
              the delta is the one to believe. */}
          {d.credits_delta_usd !== null && (
            <tr>
              <td>OpenRouter credit delta</td>
              <td className="num">{money(d.credits_delta_usd)}</td>
            </tr>
          )}
          {runs > 0 && (
            <tr>
              <td>Spend on this customer to date ({runs} runs)</td>
              <td className="num">{money(spend)}</td>
            </tr>
          )}
        </tbody>
      </table>
    </>
  );
}
